import React, { useEffect, useState } from 'react';
import {
    Receipt, DollarSign, Clock, CheckCircle,
    Plus, Search, Send,
    Building2, Calendar, FileText, Eye, Trash2,
    Loader2, CreditCard, Banknote
} from 'lucide-react';
import { useBillingStore } from '../../store/billingStore';
import { Invoice } from '../../services/billing.service';
import CreateInvoiceModal from './CreateInvoiceModal';
import RecordPaymentModal from './RecordPaymentModal';
import ViewInvoiceModal from './ViewInvoiceModal';
import ComposeEmailModal from '../communication/ComposeEmailModal';

// ============ Invoice Card Component ============
const InvoiceCard: React.FC<{
    invoice: Invoice;
    onView: () => void;
    onSend: () => void;
    onRecordPayment: () => void;
    onDelete: () => void;
    onSendReminder: () => void;
}> = ({ invoice, onView, onSend, onRecordPayment, onDelete, onSendReminder }) => {
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'draft':
                return 'from-gray-400 to-gray-500 text-white';
            case 'sent':
            case 'viewed':
                return 'from-blue-400 to-blue-600 text-white';
            case 'partial':
                return 'from-yellow-400 to-orange-500 text-white';
            case 'paid':
                return 'from-green-400 to-green-600 text-white';
            case 'overdue':
                return 'from-red-400 to-red-600 text-white';
            case 'cancelled':
                return 'from-gray-400 to-gray-500 text-white';
            default:
                return 'from-gray-400 to-gray-500 text-white';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'draft': return <FileText className="w-3.5 h-3.5" />;
            case 'sent': return <Send className="w-3.5 h-3.5" />;
            case 'viewed': return <Eye className="w-3.5 h-3.5" />;
            case 'partial': return <Clock className="w-3.5 h-3.5" />;
            case 'paid': return <CheckCircle className="w-3.5 h-3.5" />;
            case 'overdue': return <Clock className="w-3.5 h-3.5" />;
            default: return <FileText className="w-3.5 h-3.5" />;
        }
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const formatCurrency = (amount: number, currency: string = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
        }).format(amount);
    };

    const getDaysUntilDue = () => {
        const due = new Date(invoice.dueDate);
        const today = new Date();
        const diff = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return diff;
    };

    const daysUntilDue = getDaysUntilDue();

    return (
        <div className="group bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] transition-all duration-200 overflow-hidden flex flex-col h-full">
            <div className="p-6 flex-1">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                            {invoice.invoiceNumber}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{invoice.billTo.companyName}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">{invoice.billTo.email}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${getStatusColor(invoice.status)}`}>
                        {getStatusIcon(invoice.status)}
                        <span>{invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}</span>
                    </span>
                </div>

                {/* Content */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 capitalize`}>
                            {invoice.type}
                        </span>
                        <div className="text-right">
                            <div className="text-lg font-bold text-gray-900 dark:text-white">
                                {formatCurrency(invoice.total, invoice.currency)}
                            </div>
                            {invoice.amountPaid > 0 && invoice.amountPaid < invoice.total && (
                                <div className="text-xs text-green-600 dark:text-green-400">
                                    Paid: {formatCurrency(invoice.amountPaid, invoice.currency)}
                                </div>
                            )}
                        </div>
                    </div>

                    {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                        <div className={`text-sm font-medium ${daysUntilDue < 0 ? 'text-red-500' : daysUntilDue <= 7 ? 'text-yellow-500' : 'text-gray-500 dark:text-gray-400'}`}>
                            {daysUntilDue < 0
                                ? `${Math.abs(daysUntilDue)} days overdue`
                                : daysUntilDue === 0
                                    ? 'Due today'
                                    : `${daysUntilDue} days left`}
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-100 dark:border-gray-600">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={onView}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-all"
                            title="View Details"
                        >
                            <Eye className="w-4 h-4" />
                        </button>
                        {invoice.status === 'draft' && (
                            <button
                                onClick={onSend}
                                className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-all"
                                title="Send Invoice"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        )}
                        {['sent', 'viewed', 'partial', 'overdue'].includes(invoice.status) && (
                            <button
                                onClick={onRecordPayment}
                                className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-all"
                                title="Record Payment"
                            >
                                <DollarSign className="w-4 h-4" />
                            </button>
                        )}
                        {['sent', 'viewed', 'partial', 'overdue'].includes(invoice.status) && (
                            <button
                                onClick={onSendReminder}
                                className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-yellow-600 dark:hover:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 rounded-lg transition-all"
                                title="Send Reminder"
                            >
                                <Clock className="w-4 h-4" />
                            </button>
                        )}
                        <button
                            onClick={onDelete}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all"
                            title="Delete"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                        <Calendar className="w-3 h-3" />
                        <span>Due: {formatDate(invoice.dueDate)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============ Billing Settings Panel ============
const BillingSettingsPanel: React.FC = () => {
    const { settings, updateSettings, fetchSettings } = useBillingStore();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        // Business Info
        legalName: '',
        tradingName: '',
        email: '',
        phone: '',
        website: '',
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        // Tax Settings
        taxId: '',
        taxIdType: '',
        taxRegistered: false,
        defaultTaxRate: 0,
        taxName: 'Tax',
        // Invoice Defaults
        currency: 'USD',
        prefix: 'INV',
        nextNumber: 1,
        paymentTerms: 'Net 30',
        paymentTermsDays: 30,
        lateFeePercentage: 0,
        defaultNotes: '',
        defaultTerms: '',
    });

    useEffect(() => {
        if (settings) {
            setFormData({
                legalName: settings.businessInfo?.legalName || '',
                tradingName: settings.businessInfo?.tradingName || '',
                email: settings.businessInfo?.email || '',
                phone: settings.businessInfo?.phone || '',
                website: settings.businessInfo?.website || '',
                street: settings.businessInfo?.street || '',
                city: settings.businessInfo?.city || '',
                state: settings.businessInfo?.state || '',
                postalCode: settings.businessInfo?.postalCode || '',
                country: settings.businessInfo?.country || '',
                taxId: settings.taxSettings?.taxId || '',
                taxIdType: settings.taxSettings?.taxIdType || '',
                taxRegistered: settings.taxSettings?.taxRegistered || false,
                defaultTaxRate: settings.taxSettings?.defaultTaxRate || 0,
                taxName: settings.taxSettings?.taxName || 'Tax',
                currency: settings.invoiceDefaults?.currency || 'USD',
                prefix: settings.invoiceDefaults?.prefix || 'INV',
                nextNumber: settings.invoiceDefaults?.nextNumber || 1,
                paymentTerms: settings.invoiceDefaults?.paymentTerms || 'Net 30',
                paymentTermsDays: settings.invoiceDefaults?.paymentTermsDays || 30,
                lateFeePercentage: settings.invoiceDefaults?.lateFeePercentage || 0,
                defaultNotes: settings.invoiceDefaults?.notes || '',
                defaultTerms: settings.invoiceDefaults?.terms || '',
            });
        }
    }, [settings]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            // Build the update payload conditionally
            const updatePayload: any = {};

            // Only include businessInfo if required fields are filled
            if (formData.legalName.trim() && formData.email.trim()) {
                updatePayload.businessInfo = {
                    legalName: formData.legalName,
                    tradingName: formData.tradingName || undefined,
                    email: formData.email,
                    phone: formData.phone || undefined,
                    website: formData.website || undefined,
                    street: formData.street || undefined,
                    city: formData.city || undefined,
                    state: formData.state || undefined,
                    postalCode: formData.postalCode || undefined,
                    country: formData.country || undefined,
                };
            }

            // Tax settings - all optional
            updatePayload.taxSettings = {
                taxId: formData.taxId || undefined,
                taxIdType: formData.taxIdType || undefined,
                taxRegistered: formData.taxRegistered,
                defaultTaxRate: formData.defaultTaxRate,
                taxName: formData.taxName || undefined,
                regionalTaxRates: settings?.taxSettings?.regionalTaxRates || [],
            };

            // Invoice defaults - all optional
            updatePayload.invoiceDefaults = {
                currency: formData.currency,
                prefix: formData.prefix || undefined,
                nextNumber: formData.nextNumber,
                paymentTerms: formData.paymentTerms,
                paymentTermsDays: formData.paymentTermsDays,
                lateFeePercentage: formData.lateFeePercentage,
                notes: formData.defaultNotes || undefined,
                terms: formData.defaultTerms || undefined,
            };

            await updateSettings(updatePayload);
        } catch (error) {
            console.error('Failed to save settings:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const currencies = ['USD', 'EUR', 'GBP', 'AUD', 'CAD', 'INR', 'JPY', 'CNY', 'SGD', 'AED'];

    return (
        <div className="space-y-6">
            {/* Business Information */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary-500" />
                    Business Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Legal Business Name</label>
                        <input
                            type="text"
                            value={formData.legalName}
                            onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                            placeholder="Your Company LLC"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Trading Name</label>
                        <input
                            type="text"
                            value={formData.tradingName}
                            onChange={(e) => setFormData({ ...formData, tradingName: e.target.value })}
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                            placeholder="Your Brand"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                        <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                            placeholder="billing@yourcompany.com"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                        <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                            placeholder="+1 (555) 123-4567"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Website</label>
                        <input
                            type="url"
                            value={formData.website}
                            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                            placeholder="https://yourcompany.com"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Country</label>
                        <input
                            type="text"
                            value={formData.country}
                            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                            placeholder="United States"
                        />
                    </div>
                </div>
            </div>

            {/* Currency & Tax Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-500" />
                    Currency & Tax Settings
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Currency</label>
                        <select
                            value={formData.currency}
                            onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                        >
                            {currencies.map(cur => (
                                <option key={cur} value={cur}>{cur}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tax Name</label>
                        <input
                            type="text"
                            value={formData.taxName}
                            onChange={(e) => setFormData({ ...formData, taxName: e.target.value })}
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                            placeholder="GST / VAT / Sales Tax"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Tax Rate (%)</label>
                        <input
                            type="number"
                            value={formData.defaultTaxRate}
                            onChange={(e) => setFormData({ ...formData, defaultTaxRate: parseFloat(e.target.value) || 0 })}
                            min={0}
                            max={100}
                            step={0.1}
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tax ID / VAT Number</label>
                        <input
                            type="text"
                            value={formData.taxId}
                            onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                            placeholder="XX-XXXXXXX"
                        />
                    </div>
                    <div className="flex items-center gap-3 md:col-span-2">
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={formData.taxRegistered}
                                onChange={(e) => setFormData({ ...formData, taxRegistered: e.target.checked })}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:ring-4 peer-focus:ring-primary-500/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                        <span className="text-sm text-gray-700 dark:text-gray-300">Tax Registered Business</span>
                    </div>
                </div>
            </div>

            {/* Invoice Defaults */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-500" />
                    Invoice Defaults
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Invoice Prefix</label>
                        <input
                            type="text"
                            value={formData.prefix}
                            onChange={(e) => setFormData({ ...formData, prefix: e.target.value })}
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                            placeholder="INV"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Next Invoice Number</label>
                        <input
                            type="number"
                            value={formData.nextNumber}
                            onChange={(e) => setFormData({ ...formData, nextNumber: parseInt(e.target.value) || 1 })}
                            min={1}
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Late Fee (%)</label>
                        <input
                            type="number"
                            value={formData.lateFeePercentage}
                            onChange={(e) => setFormData({ ...formData, lateFeePercentage: parseFloat(e.target.value) || 0 })}
                            min={0}
                            max={100}
                            step={0.1}
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Payment Terms</label>
                        <select
                            value={formData.paymentTerms}
                            onChange={(e) => {
                                const days = e.target.value === 'Due on Receipt' ? 0 : parseInt(e.target.value.replace('Net ', ''));
                                setFormData({ ...formData, paymentTerms: e.target.value, paymentTermsDays: days });
                            }}
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                        >
                            <option value="Due on Receipt">Due on Receipt</option>
                            <option value="Net 15">Net 15</option>
                            <option value="Net 30">Net 30</option>
                            <option value="Net 45">Net 45</option>
                            <option value="Net 60">Net 60</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Notes</label>
                        <textarea
                            value={formData.defaultNotes}
                            onChange={(e) => setFormData({ ...formData, defaultNotes: e.target.value })}
                            rows={2}
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                            placeholder="Thank you for your business!"
                        />
                    </div>
                    <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Default Terms & Conditions</label>
                        <textarea
                            value={formData.defaultTerms}
                            onChange={(e) => setFormData({ ...formData, defaultTerms: e.target.value })}
                            rows={3}
                            className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none"
                            placeholder="Payment is due within the specified terms..."
                        />
                    </div>
                </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSaving ? (
                        <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <CheckCircle className="w-4 h-4" />
                            Save Settings
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

// ============ Main Billing Page ============
const BillingPage: React.FC = () => {
    const {
        stats,
        isLoading,
        activeTab,
        searchTerm,
        fetchInvoices,
        fetchStats,
        fetchPayments,
        fetchReceipts,
        deleteInvoice,
        sendReminder,
        setActiveTab,
        setSearchTerm,
        getFilteredInvoices,
        fetchSettings,
    } = useBillingStore();

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState<Invoice | null>(null);
    const [selectedViewInvoice, setSelectedViewInvoice] = useState<Invoice | null>(null);
    const [selectedEmailInvoice, setSelectedEmailInvoice] = useState<Invoice | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>('all');

    useEffect(() => {
        fetchInvoices();
        fetchStats();
        fetchSettings();
    }, [fetchInvoices, fetchStats, fetchSettings]);

    useEffect(() => {
        if (activeTab === 'payments') {
            fetchPayments();
        } else if (activeTab === 'receipts') {
            fetchReceipts();
        }
    }, [activeTab, fetchPayments, fetchReceipts]);

    const handleRecordPayment = (invoice: Invoice) => {
        setSelectedInvoiceForPayment(invoice);
        setShowPaymentModal(true);
    };

    const handleViewInvoice = (invoice: Invoice) => {
        setSelectedViewInvoice(invoice);
        setShowViewModal(true);
    };

    const handleDeleteInvoice = async (id: string) => {
        if (confirm('Are you sure you want to delete this invoice?')) {
            await deleteInvoice(id);
        }
    };

    // Handle sending invoice via email
    const handleSendInvoiceEmail = (invoice: Invoice) => {
        setSelectedEmailInvoice(invoice);
        setShowEmailModal(true);
    };

    // Generate prefilled email data for invoice
    const getInvoiceEmailData = (invoice: Invoice | null) => {
        if (!invoice) return { subject: '', body: '', to: [] as string[], attachments: [] as { name: string; url?: string; type?: string }[] };

        const formatCurrency = (amount: number, currency: string = 'USD') => {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
        };

        const formatDate = (dateStr: string) => {
            return new Date(dateStr).toLocaleDateString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric'
            });
        };

        const subject = `Invoice ${invoice.invoiceNumber} from ${invoice.billFrom.businessName}`;
        const body = `Dear ${invoice.billTo.contactName || invoice.billTo.companyName},

Please find attached invoice ${invoice.invoiceNumber} for your reference.

Invoice Summary:
• Invoice Number: ${invoice.invoiceNumber}
• Amount Due: ${formatCurrency(invoice.amountDue, invoice.currency)}
• Due Date: ${formatDate(invoice.dueDate)}
• Payment Terms: ${invoice.paymentTerms}

If you have any questions regarding this invoice, please don't hesitate to contact us.

Thank you for your business!

Best regards,
${invoice.billFrom.businessName}`;

        const attachment: { name: string; url?: string; type?: string } = {
            name: `${invoice.invoiceNumber}.pdf`,
            type: 'application/pdf'
        };
        if (invoice.pdfUrl) {
            attachment.url = invoice.pdfUrl;
        }

        return {
            subject,
            body,
            to: [invoice.billTo.email],
            attachments: [attachment]
        };
    };

    const filteredInvoices = getFilteredInvoices().filter(inv => {
        if (statusFilter === 'all') return true;
        return inv.status === statusFilter;
    });

    const tabs = [
        { id: 'invoices', label: 'Invoices', icon: <FileText className="w-4 h-4" /> },
        { id: 'payments', label: 'Payments', icon: <CreditCard className="w-4 h-4" /> },
        { id: 'receipts', label: 'Receipts', icon: <Receipt className="w-4 h-4" /> },
        { id: 'settings', label: 'Settings', icon: <Building2 className="w-4 h-4" /> },
    ];

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Billing & Invoicing</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">Manage invoices, payments, and receipts</p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                    <Plus className="w-4 h-4" />
                    New Invoice
                </button>
            </div>

            {/* Stats Overview */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-5 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl flex items-center justify-center">
                                <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                            </div>
                            <span className="text-sm text-gray-500 dark:text-gray-400">Unpaid</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            ${stats.unpaid.amount.toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{stats.unpaid.count} invoices</p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-5 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                                <Banknote className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <span className="text-sm text-gray-500 dark:text-gray-400">Pending</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            ${stats.pending.amount.toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{stats.pending.count} invoices</p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-5 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                            </div>
                            <span className="text-sm text-gray-500 dark:text-gray-400">Paid</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            ${stats.paid.amount.toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{stats.paid.count} invoices</p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-5 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
                                <DollarSign className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                            </div>
                            <span className="text-sm text-gray-500 dark:text-gray-400">Total Revenue</span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                            ${stats.totalRevenue.toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{stats.totalInvoices} total invoices</p>
                    </div>
                </div>
            )}

            {/* Tab Navigation and Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Tabs */}
                    <div className="flex bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.id
                                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                    }`}
                            >
                                {tab.icon}
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Search & Filters */}
                    {activeTab === 'invoices' && (
                        <div className="flex flex-col sm:flex-row gap-4 flex-1 max-w-xl">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search invoices..."
                                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
                                />
                            </div>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 transition-colors"
                            >
                                <option value="all">All Status</option>
                                <option value="draft">Draft</option>
                                <option value="sent">Sent</option>
                                <option value="partial">Partial</option>
                                <option value="paid">Paid</option>
                                <option value="overdue">Overdue</option>
                            </select>
                        </div>
                    )}
                </div>
            </div>

            {/* Invoices Tab */}
            {activeTab === 'invoices' && (
                <>
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
                        </div>
                    ) : filteredInvoices.length === 0 ? (
                        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-12 text-center">
                            <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-purple-100 dark:from-primary-900/30 dark:to-purple-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                                <FileText className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No invoices found</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">Create your first invoice to get started</p>
                            <button
                                onClick={() => setShowCreateModal(true)}
                                className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl hover:from-primary-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                            >
                                Create Invoice
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filteredInvoices.map((invoice) => (
                                <InvoiceCard
                                    key={invoice.id}
                                    invoice={invoice}
                                    onView={() => handleViewInvoice(invoice)}
                                    onSend={() => handleSendInvoiceEmail(invoice)}
                                    onRecordPayment={() => handleRecordPayment(invoice)}
                                    onDelete={() => handleDeleteInvoice(invoice.id)}
                                    onSendReminder={() => sendReminder(invoice.id)}
                                />
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && (
                <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-12 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <CreditCard className="w-8 h-8 text-green-600 dark:text-green-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Payment History</h3>
                    <p className="text-gray-600 dark:text-gray-400">View all payment transactions here</p>
                </div>
            )}

            {/* Receipts Tab */}
            {activeTab === 'receipts' && (
                <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-12 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <Receipt className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Payment Receipts</h3>
                    <p className="text-gray-600 dark:text-gray-400">View and manage receipts here</p>
                </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
                <BillingSettingsPanel />
            )}

            {/* Modals */}
            <CreateInvoiceModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
            />
            <RecordPaymentModal
                isOpen={showPaymentModal}
                onClose={() => {
                    setShowPaymentModal(false);
                    setSelectedInvoiceForPayment(null);
                }}
                invoice={selectedInvoiceForPayment}
            />
            <ViewInvoiceModal
                isOpen={showViewModal}
                onClose={() => {
                    setShowViewModal(false);
                    setSelectedViewInvoice(null);
                }}
                invoice={selectedViewInvoice}
                onRecordPayment={() => {
                    setShowViewModal(false);
                    if (selectedViewInvoice) {
                        handleRecordPayment(selectedViewInvoice);
                    }
                }}
                onSend={() => {
                    if (selectedViewInvoice) {
                        setShowViewModal(false);
                        handleSendInvoiceEmail(selectedViewInvoice);
                    }
                }}
            />
            <ComposeEmailModal
                isOpen={showEmailModal}
                onClose={() => {
                    setShowEmailModal(false);
                    setSelectedEmailInvoice(null);
                }}
                preselectedTemplate="builtin-invoice-template"
                prefilledTo={getInvoiceEmailData(selectedEmailInvoice).to}
                prefilledSubject={getInvoiceEmailData(selectedEmailInvoice).subject}
                prefilledBody={getInvoiceEmailData(selectedEmailInvoice).body}
                prefilledAttachments={getInvoiceEmailData(selectedEmailInvoice).attachments}
            />
        </div>
    );
};

export default BillingPage;
